// lib/telefonie/anrufKarte.ts
// Anruf-Zeile → Anzeigemodell für die Karte in der Pille. Pure Logik, damit die
// Entscheidungen (Name oder Nummer? Auswahl oder Treffer? welcher Statustext?)
// prüfbar sind — Komponententests sind in diesem Projekt nicht möglich, vitest
// läuft mit environment 'node' und erfasst nur *.test.ts.

export interface KandidatZeile {
  owner_typ: string;
  owner_id: string;
  anzeigename: string | null;
  objekt_id: string | null;
}

export interface AnrufZeile {
  id: string;
  richtung: 'eingehend' | 'ausgehend';
  status: 'klingelt' | 'aktiv' | 'beendet' | 'verpasst';
  nummer_e164: string | null;
  nummer_roh: string | null;
  treffer_typ: string | null;
  treffer_id: string | null;
  treffer_name: string | null;
  /** Bereits auf der Anruf-Zeile gesetzt (Anrufserkennung) — wird 1:1 in die
   *  Karte durchgereicht, siehe {@link AnrufKarte.objekt_id}. */
  objekt_id: string | null;
  kandidaten: KandidatZeile[] | null;
  begonnen_am: string;
  angenommen_am: string | null;
  beendet_am: string | null;
  dauer_sekunden: number | null;
}

export interface AnrufZiel {
  typ: string;
  id: string;
}

export interface AuswahlEintrag extends AnrufZiel {
  name: string;
  rolle: string;
  /** Objekt des Kandidaten, falls die Kandidatenliste eines kennt — damit eine
   *  manuelle Auswahl (AnrufContent.tsx: waehle()) dieselbe Objektzuordnung
   *  schreibt, die eine automatische Erkennung auch gesetzt hätte. */
  objekt_id: string | null;
}

export interface AnrufKarte {
  id: string;
  /** Name des Anrufers, sonst die Nummer. */
  titel: string;
  /** Lesbare Rolle, wenn erkannt. */
  rolle: string | null;
  /** Immer die Nummer, auch wenn der Titel ein Name ist. */
  nummer: string | null;
  statusText: string;
  /** Kein Treffer und keine Kandidaten — die App kennt den Anrufer nicht. */
  unbekannt: boolean;
  /** Sprungziel, wenn eindeutig erkannt. */
  ziel: AnrufZiel | null;
  /** Objekt des Anrufs, wenn die Erkennung eines kennt — additiv aus der
   *  Anruf-Zeile durchgereicht (dort ohnehin schon vorhanden), damit
   *  useAnrufKontext.ts nicht extra nachladen muss. Null bei unbekannter
   *  Nummer/Auswahl oder wenn der Anrufer keinem Objekt zugeordnet ist. */
  objekt_id: string | null;
  /** Zur Auswahl, wenn die Nummer zu verschiedenen Namen gehört. */
  auswahl: AuswahlEintrag[] | null;
  /** Anruf ist vorbei — die Karte darf nach kurzer Zeit verschwinden. */
  erledigt: boolean;
  /** Richtung, für die Iconwahl — nicht aus statusText ableiten. */
  richtung: 'eingehend' | 'ausgehend';
  /** Verpasst, für Icon und Farbe. */
  verpasst: boolean;
}

/** Platzhalter, wenn ein Kandidat keinen Anzeigenamen hat. Exportiert, damit
 *  Aufrufer (z.B. AnrufContent.tsx beim Zuordnen) ihn erkennen können, statt
 *  den String ein zweites Mal zu duplizieren. */
export const OHNE_NAMEN = 'Ohne Namen';

/** owner_typ/treffer_typ → lesbare Rolle. Exportiert, damit andere Telefonie-Module
 *  (z.B. anrufListe.ts) dieselbe Zuordnung nutzen, statt sie zu duplizieren. */
export const ROLLE: Record<string, string> = {
  mieter: 'Mieter',
  eigentuemer: 'Eigentümer',
  kontakt: 'Kontakt',
  dienstleister: 'Dienstleister',
};

/** Sekunden → m:ss. Über einer Stunde wird weitergezählt (62:05), weil eine
 *  Stundenangabe in der schmalen Karte mehr verwirrt als sie hilft. */
function dauerText(sekunden: number): string {
  const m = Math.floor(sekunden / 60);
  const s = sekunden % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function statusText(a: AnrufZeile): string {
  switch (a.status) {
    case 'klingelt':
      return a.richtung === 'ausgehend' ? 'Wird angerufen …' : 'Klingelt …';
    case 'aktiv':
      return 'Im Gespräch';
    case 'verpasst':
      return 'Verpasst';
    case 'beendet':
      return a.dauer_sekunden ? `Beendet · ${dauerText(a.dauer_sekunden)}` : 'Beendet';
  }
}

/** Anruf-Zeile in das Anzeigemodell der Karte übersetzen. */
export function zuAnrufKarte(a: AnrufZeile): AnrufKarte {
  const nummer = a.nummer_e164 ?? a.nummer_roh ?? null;
  const erkannt = !!(a.treffer_typ && a.treffer_id && (a.treffer_name || '').trim() !== '');

  // Ein gesetzter Treffer schlägt die Kandidatenliste: entweder hat die Erkennung
  // eindeutig entschieden, oder ein Mensch hat in der Karte gewählt.
  const kandidaten = !erkannt && a.kandidaten?.length ? a.kandidaten : null;

  return {
    id: a.id,
    titel: erkannt ? (a.treffer_name as string) : (nummer ?? 'Unbekannte Nummer'),
    rolle: erkannt ? (ROLLE[a.treffer_typ as string] ?? null) : null,
    nummer,
    statusText: statusText(a),
    unbekannt: !erkannt && !kandidaten,
    ziel: erkannt ? { typ: a.treffer_typ as string, id: a.treffer_id as string } : null,
    objekt_id: a.objekt_id,
    auswahl: kandidaten
      ? kandidaten.map((k) => ({
          typ: k.owner_typ,
          id: k.owner_id,
          name: (k.anzeigename || '').trim() || OHNE_NAMEN,
          rolle: ROLLE[k.owner_typ] ?? k.owner_typ,
          objekt_id: k.objekt_id,
        }))
      : null,
    erledigt: a.status === 'beendet' || a.status === 'verpasst',
    richtung: a.richtung,
    verpasst: a.status === 'verpasst',
  };
}
